import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

export type DocView = 'help' | 'privacy' | 'terms';
export type SectionId = 'services' | 'testimonials' | 'contact';

/**
 * Legacy file-style paths → their unified ?section= query equivalents.
 * Kept client-side so an inbound request that slips past the server-level
 * redirect file still resolves instead of hitting a 404.
 */
export const LEGACY_PATH_TO_SECTION: Record<string, string> = {
  '/services': 'services',
  '/testimonials': 'testimonials',
  '/contact': 'contact',
  '/help-center': 'help',
  '/privacy-policy': 'privacy',
  '/terms-of-service': 'terms',
};

const SCROLL_SECTIONS: string[] = ['services', 'testimonials', 'contact'];
const DOC_SECTIONS: string[] = ['help', 'privacy', 'terms'];

interface SectionViewContextValue {
  activeDoc: DocView | null;
  openDoc: (doc: DocView) => void;
  closeDoc: () => void;
  goToSection: (id: SectionId) => void;
}

const SectionViewContext = createContext<SectionViewContextValue | null>(null);

export function SectionViewProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Resolve the effective section from the ?section= query token first, then
  // fall back to a matching clean slash proxy path (dual route tracking).
  const querySection = searchParams.get('section');
  const cleanPath = location.pathname.replace(/\/+$/, '') || '/';
  const pathSection = LEGACY_PATH_TO_SECTION[cleanPath];
  const section = querySection ?? pathSection ?? null;

  // Dynamic state views are derived straight from the URL query/path so the
  // correct middle-area block renders on first paint.
  const activeDoc: DocView | null =
    section && DOC_SECTIONS.includes(section) ? (section as DocView) : null;

  // Single layout-level effect: resolves legacy paths, drives the document
  // state views, and smooth-scrolls to on-page sections once the DOM mounts.
  useEffect(() => {
    const path = location.pathname.replace(/\/+$/, '') || '/';

    // 1) Legacy folder-style paths → ?section= equivalents (no 404 breaks).
    const legacySection = LEGACY_PATH_TO_SECTION[path];
    if (legacySection) {
      navigate(`/?section=${legacySection}`, { replace: true });
      return undefined;
    }

    // 2) Document views (help / privacy / terms) open at the top of the page.
    if (activeDoc) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      return undefined;
    }

    // 3) Scroll targets (services / testimonials / contact) — wait for the
    //    DOM to mount, then ease the viewport to the target.
    if (section && SCROLL_SECTIONS.includes(section)) {
      const timer = window.setTimeout(() => {
        document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
      }, 160);
      return () => window.clearTimeout(timer);
    }

    return undefined;
  }, [location.pathname, section, activeDoc, navigate]);

  const openDoc = (doc: DocView) => {
    navigate(`/?section=${doc}`);
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  };

  const closeDoc = () => {
    navigate('/');
  };

  const goToSection = (id: SectionId) => {
    // Already on the home view? Just scroll. Otherwise route via the query.
    if (location.pathname === '/' && !activeDoc) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    navigate(`/?section=${id}`);
  };

  return (
    <SectionViewContext.Provider value={{ activeDoc, openDoc, closeDoc, goToSection }}>
      {children}
    </SectionViewContext.Provider>
  );
}

export function useSectionView(): SectionViewContextValue {
  const ctx = useContext(SectionViewContext);
  if (!ctx) {
    throw new Error('useSectionView must be used within a SectionViewProvider');
  }
  return ctx;
}